"use client";

import { forwardRef, type TextareaHTMLAttributes } from "react";
import styles from "./Form.module.css";
import { useFieldContext } from "./Field";

/**
 * Textarea — `<textarea>` z brand-styled stanami + floating label.
 *
 * **Floating label dla textarea:** label jest ZAWSZE u góry (mały, color muted
 * gdy puste, color primary gdy focus/wypełnione). Nie ma sensu centrowanego
 * pionowo dla multi-line pola. CSS w `Form.module.css` (`.textarea ~ .label`)
 * obsługuje to automatycznie.
 *
 * **Auto-binding z `<Field>`:** id, aria-*, placeholder=" " (wymagane dla
 * floating). Konsument może nadpisać przez własny `placeholder="..."`.
 *
 * **Char counter (opcjonalny):** gdy `maxLength` + `showCounter` ustawione,
 * renderuje `<span>` pod textareą pokazujący `current / max`. Counter
 * staje się czerwony przy >= 90% limitu.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { invalid, success, showCounter, className, ...rest },
    ref,
  ) {
    const ctx = useFieldContext();
    const effectiveInvalid = invalid ?? ctx?.invalid ?? false;
    const stateClass = effectiveInvalid
      ? styles.invalid
      : success
        ? styles.success
        : "";

    const effectivePlaceholder =
      rest.placeholder ?? (ctx?.inField ? " " : undefined);

    const currentLen =
      typeof rest.value === "string" || typeof rest.value === "number"
        ? String(rest.value).length
        : 0;
    const max = rest.maxLength;
    const overWarning =
      typeof max === "number" && currentLen >= Math.floor(max * 0.9);

    return (
      <>
        <textarea
          ref={ref}
          {...rest}
          id={rest.id ?? ctx?.id}
          aria-describedby={rest["aria-describedby"] ?? ctx?.describedBy}
          aria-invalid={rest["aria-invalid"] ?? (effectiveInvalid || undefined)}
          aria-required={rest["aria-required"] ?? (ctx?.required || undefined)}
          placeholder={effectivePlaceholder}
          className={[styles.control, styles.textarea, stateClass, className]
            .filter(Boolean)
            .join(" ")}
        />
        {showCounter && typeof max === "number" && (
          <span
            className={`${styles.counter}${overWarning ? ` ${styles.counterOver}` : ""}`}
            aria-live="polite"
          >
            {currentLen} / {max}
          </span>
        )}
      </>
    );
  },
);

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
  success?: boolean;
  /** Pokaż licznik znaków pod textareą (wymaga `maxLength`). */
  showCounter?: boolean;
};
